import {
  dayOfWeekFromKey,
  type IntencaoDoStatus,
  JANELA_MAXIMA_DO_GANTT_EM_DIAS,
  localDayKey,
  MOTIVOS_DE_BLOQUEIO,
  type MotivoDeBloqueio,
  ROTULO_DO_MOTIVO_DE_BLOQUEIO,
  ROTULO_DO_STATUS_DE_RESERVA,
  STATUS_DE_VEICULO,
  type StatusDoVeiculo,
} from '@locaveri/shared'
import { Link } from '@tanstack/react-router'
import { GripVertical } from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { identificacaoDoVeiculo } from '@/features/frota/api'
import { BadgeDeStatusDoVeiculo } from '@/features/frota/badges'
import { rotuloDosDiasDaSemana } from '@/features/precos/api'
import { formatarData, formatarDataHora } from '@/lib/datas'

import type { GanttDaFrota, LinhaDoGantt, ReservaSemVeiculo, TrechoDoGantt } from './api'
import {
  DialogoDeRealocacao,
  type DestinoDeRealocacao,
  type PedidoDeRealocacao,
} from './dialogo-de-realocacao'

/**
 * O calendário da frota (Gantt) — CSS Grid puro, sem biblioteca.
 *
 * ---------------------------------------------------------------------------
 * UMA GRADE SÓ, TUDO POSICIONADO À MÃO
 * ---------------------------------------------------------------------------
 * Cabeçalho, coluna fixa, fundos de dia e barras vivem no MESMO grid, cada um
 * com `gridRow`/`gridColumn` explícitos. Um grid por linha desalinharia as
 * colunas (cada linha resolveria o próprio `minmax`), e auto-placement é
 * impossível aqui: as barras ocupam células que os fundos já ocupam — sem
 * endereço explícito, o grid empurraria tudo para a linha seguinte.
 *
 * A coluna do veículo é `position: sticky`: o tempo rola no contêiner
 * `overflow-x-auto` PRÓPRIO do calendário (a página nunca rola de lado) e a
 * placa continua visível — sem ela, a barra da semana 3 é de um carro anônimo.
 *
 * ---------------------------------------------------------------------------
 * AMARELO SÓ NA RESERVA
 * ---------------------------------------------------------------------------
 * A barra de ALOCACAO/RESERVA é `--brand-500` com TEXTO PRETO — é onde o olho
 * do gestor deve parar, e é um link para o detalhe da reserva. Manutenção,
 * limpeza e demais motivos são cinza com o rótulo escrito: pintá-los de cores
 * viraria arco-íris e o dado principal deixaria de saltar. Nenhum status
 * depende só de cor — toda barra carrega texto, e o `title` traz o resto.
 *
 * ---------------------------------------------------------------------------
 * O DRAG É GESTO, NUNCA OPERAÇÃO
 * ---------------------------------------------------------------------------
 * A barra de alocação de uma reserva que ainda não saiu do pátio
 * (`VEICULO_ALOCADO` / `RETIRADA_PENDENTE`) tem uma ALÇA que arrasta para a
 * linha de outro veículo da MESMA categoria. Soltar não realoca nada: abre o
 * diálogo que exige o motivo do desfazimento e a justificativa da escolha
 * manual — a realocação continua passando pela máquina de estados, com as duas
 * pontas no audit_log; o arrasto só preenche o formulário. A mesma ação existe
 * sem drag (clique na alça, ou Enter com o foco nela) com um seletor de
 * destino — e Esc cancela o arrasto no meio. Pointer events nativos, sem
 * biblioteca.
 *
 * ---------------------------------------------------------------------------
 * FRAÇÃO DE DIA ARREDONDA PARA O DIA
 * ---------------------------------------------------------------------------
 * A resolução visual é o DIA LOCAL (`America/Sao_Paulo`): um trecho de 15/03
 * 14h a 18/03 10h pinta os quatro dias inteiros. A exceção é o fim exatamente
 * à meia-noite local, que NÃO ocupa o dia que começa ali — o range é `[)`, e
 * 1 ms para trás resolve sem aritmética de fuso.
 */

const UM_DIA_EM_MS = 86_400_000

const FIM_DE_SEMANA = new Set([0, 6])

/** Dia local (`AAAA-MM-DD`) de um instante ISO; `null` quando não é data. */
function diaLocal(iso: string): string | null {
  const data = new Date(iso)

  return Number.isNaN(data.getTime()) ? null : localDayKey(data)
}

/**
 * Meio-dia UTC do dia de calendário. É a âncora de toda a aritmética de dias
 * daqui: somar 24h ao meio-dia nunca cruza a fronteira do dia errado, mesmo se
 * o horário de verão voltar.
 */
function meioDiaUtc(dia: string): number {
  return Date.parse(`${dia}T12:00:00Z`)
}

/** Posição do dia em relação ao primeiro da janela. Negativo = antes dela. */
function indiceDoDia(dia: string, primeiroDia: string): number {
  return Math.round((meioDiaUtc(dia) - meioDiaUtc(primeiroDia)) / UM_DIA_EM_MS)
}

/**
 * Os dias de calendário da janela, do primeiro ao último instante devolvido
 * pela API. O teto de `JANELA_MAXIMA_DO_GANTT_EM_DIAS` é defesa em camada: a
 * API já recusa janela maior, mas uma resposta corrompida não pode virar um
 * grid de dez mil colunas.
 */
function eixoDeDias(deIso: string, ateIso: string): readonly string[] {
  const primeiro = diaLocal(deIso)
  const ultimo = diaLocal(ateIso)

  if (primeiro === null || ultimo === null) {
    return []
  }

  const inicio = meioDiaUtc(primeiro)
  const fim = meioDiaUtc(ultimo)

  if (Number.isNaN(inicio) || Number.isNaN(fim) || fim < inicio) {
    return []
  }

  const total = Math.min(
    Math.round((fim - inicio) / UM_DIA_EM_MS) + 1,
    JANELA_MAXIMA_DO_GANTT_EM_DIAS,
  )

  return Array.from({ length: total }, (_, indice) =>
    new Date(inicio + indice * UM_DIA_EM_MS).toISOString().slice(0, 10),
  )
}

interface PosicaoDaBarra {
  /** Índice do primeiro dia ocupado dentro da janela. */
  readonly de: number
  /** Índice do último dia ocupado (inclusive). */
  readonly ate: number
}

/**
 * Onde a barra do trecho cai na janela. `null` quando o trecho está inteiro
 * fora dela (a API devolve o que TOCA a janela, então as pontas podem vazar) ou
 * quando as datas não são datas.
 */
function posicaoDoTrecho(
  trecho: TrechoDoGantt,
  primeiroDia: string,
  totalDias: number,
): PosicaoDaBarra | null {
  const inicio = new Date(trecho.inicio)
  const fim = new Date(trecho.fim)

  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime())) {
    return null
  }

  // Fim exatamente à meia-noite local não ocupa o dia que começa ali.
  const fimAjustado = fim.getTime() > inicio.getTime() ? new Date(fim.getTime() - 1) : inicio
  const de = indiceDoDia(localDayKey(inicio), primeiroDia)
  const ate = indiceDoDia(localDayKey(fimAjustado), primeiroDia)

  if (ate < 0 || de > totalDias - 1) {
    return null
  }

  return { de: Math.max(de, 0), ate: Math.min(ate, totalDias - 1) }
}

/**
 * `motivo` e `statusAtual` chegam como string solta (decisão do DTO: um valor
 * novo do lado da API não pode derrubar o calendário). As guardas devolvem o
 * vocabulário conhecido ao tipo — e o valor desconhecido aparece cru, que é
 * melhor do que sumir.
 */
function ehMotivoConhecido(motivo: string): motivo is MotivoDeBloqueio {
  return (MOTIVOS_DE_BLOQUEIO as readonly string[]).includes(motivo)
}

function rotuloDoMotivo(motivo: string): string {
  return ehMotivoConhecido(motivo) ? ROTULO_DO_MOTIVO_DE_BLOQUEIO[motivo] : motivo
}

function ehStatusDeVeiculo(status: string): status is StatusDoVeiculo {
  return (STATUS_DE_VEICULO as readonly string[]).includes(status)
}

/** O tooltip nativo da barra — o detalhe que não cabe dentro dela. */
function dicaDoTrecho(trecho: TrechoDoGantt): string {
  const periodo = `${formatarDataHora(trecho.inicio)} → ${formatarDataHora(trecho.fim)}`

  if (trecho.reserva === null) {
    return `${rotuloDoMotivo(trecho.motivo)} · ${periodo}`
  }

  const status = ROTULO_DO_STATUS_DE_RESERVA[trecho.reserva.status].rotulo

  return `Reserva #${String(trecho.reserva.numero)} · ${trecho.reserva.contaNome} · ${status} · ${periodo}`
}

/** Mesmo mapa de `features/frota/badges` — a intenção vem do domínio, a cor é da tela. */
const VARIANTE_DA_INTENCAO = {
  positiva: 'success',
  informativa: 'info',
  neutra: 'neutral',
  atencao: 'warning',
  critica: 'danger',
} as const satisfies Readonly<Record<IntencaoDoStatus, string>>

// ---------------------------------------------------------------------------
// A raia "a alocar"
// ---------------------------------------------------------------------------

/**
 * Reservas que já seguram uma unidade da categoria e ainda não têm carro — no
 * caixa, em análise ou aprovadas. Elas ocupam capacidade da CATEGORIA (a regra
 * de ouro) e por isso não têm linha na grade — a raia embaixo é o trabalho
 * pendente da operação, e cada cartão leva ao detalhe da reserva, onde a
 * alocação acontece. O selo de status separa o que já pode ser alocado do que
 * ainda espera pagamento ou aprovação.
 */
function RaiaAAlocar({
  reservas,
}: {
  readonly reservas: readonly ReservaSemVeiculo[]
}): ReactElement {
  return (
    <section aria-labelledby="titulo-a-alocar" className="flex flex-col gap-3">
      <div>
        <h3 id="titulo-a-alocar" className="text-sm font-semibold tracking-tight text-foreground">
          A alocar
        </h3>
        <p className="text-xs text-fg-secondary">
          Reservas no caixa, em análise ou aprovadas, ainda sem veículo — ocupam a capacidade da
          categoria mesmo sem placa.
        </p>
      </div>

      {reservas.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-surface p-4 text-sm text-fg-muted">
          Nenhuma reserva esperando veículo nesta janela.
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {reservas.map((reserva) => {
            const status = ROTULO_DO_STATUS_DE_RESERVA[reserva.status]

            return (
              <li key={reserva.id} className="min-w-0">
                <Link
                  to="/reservas/$reservaId"
                  params={{ reservaId: reserva.id }}
                  className="flex h-full flex-col gap-1 rounded-lg border border-border bg-surface p-3 transition-colors hover:bg-elevated"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate text-sm font-medium text-foreground">
                      #{reserva.numero} · {reserva.contaNome}
                    </p>
                    <Badge variant={VARIANTE_DA_INTENCAO[status.intencao]}>{status.rotulo}</Badge>
                  </div>
                  <p className="truncate text-xs text-fg-secondary">{reserva.categoriaNome}</p>
                  <p className="text-xs tabular-money text-fg-muted">
                    {formatarDataHora(reserva.retirada)} → {formatarDataHora(reserva.devolucao)}
                  </p>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Realocação por arrasto
// ---------------------------------------------------------------------------

/**
 * Status em que a reserva ainda não saiu do pátio — só nesses o carro troca
 * pela agenda (em `RETIRADA_PENDENTE` o contrato é cancelado junto, e o
 * diálogo avisa). O payload do Gantt traz o status da reserva; a barra de um
 * status fora desta lista simplesmente não ganha alça.
 */
const STATUS_REALOCAVEIS = new Set<string>(['VEICULO_ALOCADO', 'RETIRADA_PENDENTE'])

/** Abaixo disso o gesto é clique; acima, arrasto. Evita drag acidental. */
const LIMIAR_DE_ARRASTO_EM_PX = 5

type FaseDoArrasto = 'inicio' | 'mover' | 'soltar' | 'cancelar'

type ReservaDoTrecho = NonNullable<TrechoDoGantt['reserva']>

interface ArrastoAtivo {
  readonly reserva: ReservaDoTrecho
  readonly origem: DestinoDeRealocacao
  /** A linha sob o ponteiro que pode receber a reserva; `null` fora de todas. */
  readonly alvoId: string | null
}

/** O recorte de uma linha do Gantt que o diálogo de realocação entende. */
function paraDestino(linha: LinhaDoGantt): DestinoDeRealocacao {
  return {
    vehicleId: linha.vehicleId,
    placa: linha.placa,
    modelo: linha.modelo,
    categoriaNome: linha.categoriaNome,
  }
}

interface AlcaDeRealocacaoProps {
  readonly rotulo: string
  /** Clique (ou Enter/Espaço): o diálogo abre com o SELETOR de destino. */
  readonly aoClicar: () => void
  readonly aoArrastar: (fase: FaseDoArrasto, x: number, y: number) => void
}

/**
 * A alça de arrasto da barra amarela — pointer events nativos, sem biblioteca.
 *
 * Clique e arrasto saem do MESMO botão, separados pelo limiar de movimento: um
 * `pointerdown` que anda menos que o limiar é clique (abre o diálogo com o
 * seletor); passou do limiar, vira arrasto com `setPointerCapture` — que
 * mantém os `pointermove` chegando aqui mesmo com o ponteiro sobre outra
 * linha. O botão também é o caminho SEM mouse: Enter/Espaço chegam como
 * `click` de `detail === 0` e abrem o mesmo diálogo.
 */
function AlcaDeRealocacao({ rotulo, aoClicar, aoArrastar }: AlcaDeRealocacaoProps): ReactElement {
  const gesto = useRef<{ pointerId: number; x0: number; y0: number; arrastando: boolean } | null>(
    null,
  )

  return (
    <button
      type="button"
      aria-label={rotulo}
      title="Realocar — arraste para outro veículo ou clique para escolher"
      className="absolute inset-y-0 left-0 flex w-6 cursor-grab touch-none select-none items-center justify-center rounded-l-md text-primary-foreground/70 opacity-0 transition-opacity hover:text-primary-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing group-hover/barra:opacity-100"
      onPointerDown={(evento) => {
        if (evento.pointerType === 'mouse' && evento.button !== 0) {
          return
        }

        // Sem isto o navegador inicia seleção de texto (e o drag nativo do
        // link vizinho) junto com o nosso arrasto.
        evento.preventDefault()
        evento.currentTarget.setPointerCapture(evento.pointerId)
        gesto.current = {
          pointerId: evento.pointerId,
          x0: evento.clientX,
          y0: evento.clientY,
          arrastando: false,
        }
      }}
      onPointerMove={(evento) => {
        const atual = gesto.current

        if (atual === null) {
          return
        }

        if (evento.pointerId !== atual.pointerId) {
          return
        }

        if (!atual.arrastando) {
          if (
            Math.hypot(evento.clientX - atual.x0, evento.clientY - atual.y0) <
            LIMIAR_DE_ARRASTO_EM_PX
          ) {
            return
          }

          atual.arrastando = true
          aoArrastar('inicio', evento.clientX, evento.clientY)
        }

        aoArrastar('mover', evento.clientX, evento.clientY)
      }}
      onPointerUp={(evento) => {
        const atual = gesto.current

        if (atual === null) {
          return
        }

        if (evento.pointerId !== atual.pointerId) {
          return
        }

        gesto.current = null

        if (atual.arrastando) {
          aoArrastar('soltar', evento.clientX, evento.clientY)
        } else {
          aoClicar()
        }
      }}
      onPointerCancel={(evento) => {
        const atual = gesto.current

        if (atual === null) {
          return
        }

        if (evento.pointerId !== atual.pointerId) {
          return
        }

        gesto.current = null

        if (atual.arrastando) {
          aoArrastar('cancelar', evento.clientX, evento.clientY)
        }
      }}
      onClick={(evento) => {
        // Enter/Espaço chegam como click sem ponteiro (`detail === 0`) — é o
        // caminho do teclado. O click de mouse já foi tratado no pointerup.
        if (evento.detail === 0) {
          aoClicar()
        }
      }}
    >
      <GripVertical className="size-3.5" aria-hidden="true" />
    </button>
  )
}

// ---------------------------------------------------------------------------
// A grade
// ---------------------------------------------------------------------------

export interface GanttProps {
  readonly dados: GanttDaFrota
}

export function Gantt({ dados }: GanttProps): ReactElement {
  const dias = useMemo(() => eixoDeDias(dados.de, dados.ate), [dados.de, dados.ate])
  const primeiroDia = dias[0]

  // ------------------------------------------------------------------
  // Realocação: o arrasto em curso e o pedido que o diálogo confirma.
  //
  // O `arrastoRef` espelha o estado porque o `soltar` chega pelo pointer
  // capture DEPOIS de um eventual Esc — o handler precisa do valor atual,
  // não do valor do render que o criou. A posição do fantasma não é estado
  // de propósito: reposicionar via ref não re-renderiza a grade inteira a
  // cada pixel de movimento; só a troca de linha-alvo re-renderiza.
  // ------------------------------------------------------------------
  const [arrasto, setArrasto] = useState<ArrastoAtivo | null>(null)
  const [pedido, setPedido] = useState<PedidoDeRealocacao | null>(null)
  const arrastoRef = useRef<ArrastoAtivo | null>(null)
  const linhasDeVeiculo = useRef(new Map<string, HTMLDivElement>())
  const fantasmaRef = useRef<HTMLDivElement | null>(null)
  const pontoRef = useRef({ x: 0, y: 0 })

  const arrastando = arrasto !== null

  // Esc cancela o arrasto no meio; o `soltar` que ainda vier do pointer
  // capture encontra o ref vazio e não faz nada.
  useEffect(() => {
    if (!arrastando) {
      return undefined
    }

    function aoTeclar(evento: KeyboardEvent): void {
      if (evento.key === 'Escape') {
        arrastoRef.current = null
        setArrasto(null)
      }
    }

    window.addEventListener('keydown', aoTeclar)

    return () => {
      window.removeEventListener('keydown', aoTeclar)
    }
  }, [arrastando])

  // O seletor do diálogo oferece o que o CALENDÁRIO mostra: as linhas da
  // mesma categoria da origem — a regra de ouro; categoria diferente a API
  // recusaria de qualquer forma (CATEGORIA_DIFERENTE é bloqueio duro).
  const destinosDoPedido = useMemo(() => {
    if (pedido === null) {
      return []
    }

    return dados.veiculos
      .filter(
        (veiculo) =>
          veiculo.vehicleId !== pedido.origem.vehicleId &&
          veiculo.categoriaNome === pedido.origem.categoriaNome,
      )
      .map(paraDestino)
  }, [pedido, dados.veiculos])

  function mudarArrasto(valor: ArrastoAtivo | null): void {
    arrastoRef.current = valor
    setArrasto(valor)
  }

  function posicionarFantasma(x: number, y: number): void {
    pontoRef.current = { x, y }
    const fantasma = fantasmaRef.current

    if (fantasma !== null) {
      fantasma.style.left = `${String(x + 14)}px`
      fantasma.style.top = `${String(y)}px`
    }
  }

  /**
   * A linha de veículo sob o ponteiro que PODE receber a reserva. A conta é
   * pela banda vertical da célula fixa de cada linha (viewport-relativa, como
   * o `clientY`), então funciona com qualquer rolagem horizontal.
   */
  function alvoNoPonto(y: number, origem: DestinoDeRealocacao): LinhaDoGantt | null {
    for (const veiculo of dados.veiculos) {
      if (
        veiculo.vehicleId === origem.vehicleId ||
        veiculo.categoriaNome !== origem.categoriaNome
      ) {
        continue
      }

      const elemento = linhasDeVeiculo.current.get(veiculo.vehicleId)

      if (elemento === undefined) {
        continue
      }

      const caixa = elemento.getBoundingClientRect()

      if (y >= caixa.top && y < caixa.bottom) {
        return veiculo
      }
    }

    return null
  }

  function abrirDialogo(
    reserva: ReservaDoTrecho,
    origem: DestinoDeRealocacao,
    destino: DestinoDeRealocacao | null,
  ): void {
    setPedido({
      reservaId: reserva.id,
      numero: reserva.numero,
      status: reserva.status,
      contaNome: reserva.contaNome,
      origem,
      destino,
    })
  }

  function tratarArrasto(
    reserva: ReservaDoTrecho,
    origem: DestinoDeRealocacao,
    fase: FaseDoArrasto,
    x: number,
    y: number,
  ): void {
    if (fase === 'inicio') {
      posicionarFantasma(x, y)
      mudarArrasto({ reserva, origem, alvoId: null })
      return
    }

    const atual = arrastoRef.current

    if (atual === null) {
      return
    }

    if (fase === 'cancelar') {
      mudarArrasto(null)
      return
    }

    const alvo = alvoNoPonto(y, atual.origem)

    if (fase === 'mover') {
      posicionarFantasma(x, y)
      const alvoId = alvo?.vehicleId ?? null

      if (alvoId !== atual.alvoId) {
        mudarArrasto({ ...atual, alvoId })
      }

      return
    }

    // Soltar: fora de uma linha válida é o mesmo que cancelar.
    mudarArrasto(null)

    if (alvo !== null) {
      abrirDialogo(atual.reserva, atual.origem, paraDestino(alvo))
    }
  }

  const alvoIdDoArrasto = arrasto?.alvoId ?? null
  const linhaAlvo =
    alvoIdDoArrasto === null
      ? undefined
      : dados.veiculos.find((veiculo) => veiculo.vehicleId === alvoIdDoArrasto)

  // Só acontece com resposta corrompida — a API recusa janela vazia. Ainda
  // assim, a raia de pendências continua de pé: ela não depende do eixo.
  if (primeiroDia === undefined) {
    return (
      <div className="flex flex-col gap-4">
        <p className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-fg-secondary">
          A janela devolvida pelo servidor não tem nenhum dia — tente mudar o período.
        </p>
        <RaiaAAlocar reservas={dados.naoAlocadas} />
      </div>
    )
  }

  const totalDias = dias.length
  const indiceDeHoje = indiceDoDia(localDayKey(new Date()), primeiroDia)
  const hojeNaJanela = indiceDeHoje >= 0 && indiceDeHoje < totalDias
  const fimDeSemanaPorDia = dias.map((dia) => FIM_DE_SEMANA.has(dayOfWeekFromKey(dia)))

  // A coluna do veículo é fixa; cada dia tem um mínimo que força o scroll
  // horizontal do PRÓPRIO contêiner quando a janela não cabe — nunca da página.
  const modeloDeColunas = `13rem repeat(${String(totalDias)}, minmax(2.75rem, 1fr))`

  /**
   * A linha vertical de hoje é a borda esquerda reforçada da coluna do dia.
   * A largura é exclusiva (`border-l` OU `border-l-2`, nunca as duas): as duas
   * classes disputam a mesma propriedade e quem venceria seria a ordem do
   * stylesheet, não a intenção.
   */
  function classesDoDia(indice: number, base: string): string {
    const borda =
      hojeNaJanela && indice === indiceDeHoje ? 'border-l-2 border-l-info-fg' : 'border-l'
    const fimDeSemana = fimDeSemanaPorDia[indice] === true ? ' bg-elevated' : ''

    return `${base} ${borda}${fimDeSemana}`
  }

  return (
    <div className="flex flex-col gap-4">
      {/* A legenda é texto, não só cor: quem não distingue amarelo de cinza lê. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-secondary">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="size-3 rounded-sm bg-primary" />
          Reserva / alocação — clique abre a reserva
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="size-3 rounded-sm border border-border-strong bg-elevated"
          />
          Manutenção, limpeza e demais bloqueios
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-3 w-0.5 bg-info-fg" />
          Hoje
        </span>
        <span className="inline-flex items-center gap-1.5">
          <GripVertical aria-hidden="true" className="size-3 text-fg-muted" />
          Alça (no hover): arraste a reserva para outro veículo da categoria, ou clique para
          escolher
        </span>
      </div>

      {dados.veiculos.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-surface p-6 text-center text-sm text-fg-secondary">
          Nenhum veículo neste recorte — tire o filtro de categoria para ver a frota inteira.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <div className="grid" style={{ gridTemplateColumns: modeloDeColunas }}>
            {/* ------------------------------------------------------------
                Cabeçalho — um dia por coluna
                ------------------------------------------------------------ */}
            <div
              className="sticky left-0 z-30 border-b border-r border-border bg-surface px-3 py-2 text-xs font-medium text-fg-secondary"
              style={{ gridRow: 1, gridColumn: 1 }}
            >
              Veículo
            </div>

            {dias.map((dia, indice) => (
              <div
                key={dia}
                className={classesDoDia(
                  indice,
                  'border-b border-border px-1 py-2 text-center text-xs tabular-money text-fg-secondary',
                )}
                style={{ gridRow: 1, gridColumn: indice + 2 }}
              >
                <p>{rotuloDosDiasDaSemana([dayOfWeekFromKey(dia)])}</p>
                <p className="text-foreground">{formatarData(dia).slice(0, 5)}</p>
                {hojeNaJanela && indice === indiceDeHoje ? (
                  <p className="font-medium text-info-fg">hoje</p>
                ) : null}
              </div>
            ))}

            {/* ------------------------------------------------------------
                Uma linha por veículo: coluna fixa + fundos de dia + barras
                ------------------------------------------------------------ */}
            {dados.veiculos.map((veiculo, indiceDaLinha) => {
              const linha = indiceDaLinha + 2

              return (
                <div key={veiculo.vehicleId} className="contents">
                  <div
                    // A célula fixa é a régua vertical da linha para o arrasto:
                    // o rect dela diz se o ponteiro está sobre este veículo.
                    ref={(elemento) => {
                      if (elemento === null) {
                        linhasDeVeiculo.current.delete(veiculo.vehicleId)
                      } else {
                        linhasDeVeiculo.current.set(veiculo.vehicleId, elemento)
                      }
                    }}
                    className="sticky left-0 z-20 flex min-w-0 flex-col items-start gap-1 border-r border-t border-border bg-surface px-3 py-2"
                    style={{ gridRow: linha, gridColumn: 1 }}
                  >
                    <p className="w-full truncate text-sm font-medium text-foreground">
                      {identificacaoDoVeiculo(veiculo)}
                    </p>
                    <p className="w-full truncate text-xs text-fg-secondary">
                      {veiculo.categoriaNome}
                    </p>
                    {ehStatusDeVeiculo(veiculo.statusAtual) ? (
                      <BadgeDeStatusDoVeiculo status={veiculo.statusAtual} />
                    ) : (
                      <Badge variant="neutral">{veiculo.statusAtual}</Badge>
                    )}
                  </div>

                  {dias.map((dia, indice) => (
                    <div
                      key={dia}
                      aria-hidden="true"
                      className={classesDoDia(indice, 'border-t border-border')}
                      style={{ gridRow: linha, gridColumn: indice + 2 }}
                    />
                  ))}

                  {veiculo.trechos.map((trecho) => {
                    const posicao = posicaoDoTrecho(trecho, primeiroDia, totalDias)

                    if (posicao === null) {
                      return null
                    }

                    const estilo = {
                      gridRow: linha,
                      gridColumn: `${String(posicao.de + 2)} / ${String(posicao.ate + 3)}`,
                    }
                    const chave = `${trecho.inicio}|${trecho.fim}|${trecho.motivo}`

                    // Barra de reserva/alocação: amarelo da marca com texto
                    // PRETO (regra de contraste do CLAUDE.md) e link para o
                    // detalhe. Quando a reserva ainda não saiu do pátio, a
                    // barra ganha a ALÇA de realocação — o link continua sendo
                    // o resto da barra.
                    if (trecho.reserva !== null) {
                      const reserva = trecho.reserva
                      const realocavel =
                        trecho.motivo === 'ALOCACAO' && STATUS_REALOCAVEIS.has(reserva.status)
                      const emArrasto = arrasto !== null && arrasto.reserva.id === reserva.id

                      return (
                        <div
                          key={chave}
                          className={`group/barra relative z-10 m-1 flex min-w-0${emArrasto ? ' opacity-60' : ''}`}
                          style={estilo}
                        >
                          <Link
                            to="/reservas/$reservaId"
                            params={{ reservaId: reserva.id }}
                            title={dicaDoTrecho(trecho)}
                            className={`flex min-w-0 flex-1 items-center overflow-hidden rounded-md bg-primary py-1 pr-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-brand-400 ${realocavel ? 'pl-6' : 'pl-2'}`}
                          >
                            <span className="truncate">
                              #{reserva.numero} · {reserva.contaNome}
                            </span>
                          </Link>

                          {realocavel ? (
                            <AlcaDeRealocacao
                              rotulo={`Realocar a reserva nº ${String(reserva.numero)} (${veiculo.placa})`}
                              aoClicar={() => {
                                abrirDialogo(reserva, paraDestino(veiculo), null)
                              }}
                              aoArrastar={(fase, x, y) => {
                                tratarArrasto(reserva, paraDestino(veiculo), fase, x, y)
                              }}
                            />
                          ) : null}
                        </div>
                      )
                    }

                    return (
                      <div
                        key={chave}
                        title={dicaDoTrecho(trecho)}
                        className="z-10 m-1 flex min-w-0 items-center overflow-hidden rounded-md border border-border-strong bg-elevated px-2 py-1 text-xs text-fg-secondary"
                        style={estilo}
                      >
                        <span className="truncate">{rotuloDoMotivo(trecho.motivo)}</span>
                      </div>
                    )
                  })}

                  {/* Durante o arrasto, as linhas que PODEM receber a reserva
                      (mesma categoria, outro carro) ganham a moldura de alvo;
                      a que está sob o ponteiro acende. `pointer-events-none`:
                      quem decide o alvo é a conta por retângulo, não o DOM. */}
                  {arrasto !== null &&
                  arrasto.origem.vehicleId !== veiculo.vehicleId &&
                  veiculo.categoriaNome === arrasto.origem.categoriaNome ? (
                    <div
                      aria-hidden="true"
                      className={
                        arrasto.alvoId === veiculo.vehicleId
                          ? 'pointer-events-none z-10 m-0.5 rounded-md border-2 border-brand-500 bg-brand-500/10'
                          : 'pointer-events-none z-10 m-0.5 rounded-md border border-dashed border-border-strong'
                      }
                      style={{ gridRow: linha, gridColumn: '2 / -1' }}
                    />
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <RaiaAAlocar reservas={dados.naoAlocadas} />

      {/* O fantasma que segue o ponteiro. A posição inicial vem do render; os
          movimentos seguintes reposicionam via ref, sem re-render da grade. */}
      {arrasto !== null ? (
        <div
          ref={fantasmaRef}
          aria-hidden="true"
          className="pointer-events-none fixed z-50 -translate-y-1/2 whitespace-nowrap rounded-md border border-brand-500 bg-elevated px-2 py-1 text-xs font-medium text-foreground shadow-overlay"
          style={{ left: pontoRef.current.x + 14, top: pontoRef.current.y }}
        >
          Reserva nº {arrasto.reserva.numero} —{' '}
          {linhaAlvo === undefined
            ? `solte numa linha de ${arrasto.origem.categoriaNome} (Esc cancela)`
            : `mover para ${linhaAlvo.placa}`}
        </div>
      ) : null}

      <DialogoDeRealocacao
        pedido={pedido}
        destinos={destinosDoPedido}
        aoFechar={() => {
          setPedido(null)
        }}
      />
    </div>
  )
}
